import posthog from 'posthog-js';

import { config } from 'config';
import { IUser } from 'common/interfaces/iuser';
import UILogger from 'common/utils/logger';

let initialized = false;

/**
 * App identifier for analytics tagging. Combined with the env it forms the
 * `app_env` tag family used as dashboard filters:
 *   xr-web-admin-dev | xr-web-admin-stage | xr-web-admin-prod
 * (the React Native app will register xr-frontend-ios-* the same way).
 */
const ANALYTICS_APP_ID = 'xr-web-admin';

/**
 * Initialize PostHog. Safe to call once at app startup; a missing key or a
 * disabled env turns every analytics call into a no-op.
 *
 * Pageviews (SPA route changes) and pageleaves (used by PostHog to compute
 * time-on-page) are captured automatically via the '2025-05-24' defaults,
 * which track history.pushState/replaceState navigation.
 */
export const initAnalytics = () => {
  const { enabled, posthogKey, posthogHost } = config.analytics;
  if (!enabled || !posthogKey) {
    UILogger.info('analytics: disabled (no key or env disabled)');
    return;
  }
  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      defaults: '2025-05-24',
      autocapture: true,
      person_profiles: 'identified_only',
    });
    // Super properties: stamped on every event so dashboards can filter by
    // app_env (exact tag), or slice across apps/envs via app + environment.
    posthog.register({
      app_env: `${ANALYTICS_APP_ID}-${config.envType}`,
      app: ANALYTICS_APP_ID,
      environment: config.envType,
    });
    initialized = true;
  } catch (e) {
    UILogger.error('analytics: init failed', e);
  }
};

/**
 * Tie all subsequent events to the logged-in user. Called after login and
 * after a persisted session is restored.
 *
 * Privacy policy: the distinct id is the user's uuId (never the numeric DB id),
 * and no personal information (email, name, username, phone) is sent to
 * PostHog — only non-identifying role/tenant attributes.
 */
export const identifyAnalyticsUser = (user: IUser | null | undefined, roleName?: string) => {
  if (!initialized || !user) return;
  if (!user.uuId) {
    UILogger.error('analytics: user has no uuId, skipping identify');
    return;
  }
  try {
    posthog.identify(user.uuId, {
      userType: user.userType,
      roleId: user.roleId,
      roleName: roleName,
      enterpriseId: user.enterpriseId,
      franchiseeId: user.franchiseeId,
    });
  } catch (e) {
    UILogger.error('analytics: identify failed', e);
  }
};

/** Unlink the device from the user; call on logout so the next login isn't mixed into the same person. */
export const resetAnalyticsUser = () => {
  if (!initialized) return;
  try {
    posthog.reset();
  } catch (e) {
    UILogger.error('analytics: reset failed', e);
  }
};

/** Capture a custom app event, e.g. trackEvent('report_exported', { reportId }). */
export const trackEvent = (event: string, properties?: Record<string, unknown>) => {
  if (!initialized) return;
  try {
    posthog.capture(event, properties);
  } catch (e) {
    UILogger.error('analytics: capture failed', e);
  }
};
